import { House, Leaf, Sparkles, Sun, BadgeCheck } from "lucide-react";
import { Card } from "@/components/ui/card";
import Reveal from "@/components/layout/Reveal";
import SectionLabel from "@/components/layout/SectionLabel";

export const MissionSection = () => {
  return (
    <section data-testid="mission-section" className="py-20 sm:py-28 bg-white relative overflow-hidden">
      <div className="absolute -top-32 -right-32 h-[380px] w-[380px] rounded-full bg-[#10B981]/8 blur-3xl pointer-events-none" />
      <div className="max-w-6xl mx-auto px-6 sm:px-8 relative">
        <Reveal className="max-w-3xl">
          <SectionLabel>Our story</SectionLabel>
          <h2 className="font-heading mt-4 text-3xl sm:text-5xl font-bold tracking-tight text-[#0B132B]">
            Our Mission: <span className="text-[#10B981]">Zero Carbon Homes</span>
          </h2>
          <p className="mt-4 text-lg text-[#475569] leading-relaxed">
            Helping households transition to smarter, cleaner and more energy-efficient living.
          </p>
        </Reveal>

        <div className="mt-12 grid lg:grid-cols-12 gap-8">
          <Reveal className="lg:col-span-7">
            <div className="space-y-5 text-[#475569] leading-relaxed">
              <p>
                AeroMax Energy was created from a simple belief: the homes of tomorrow should be cleaner, smarter and more efficient than the homes of yesterday.
              </p>
              <p>
                Our founders come from a smart home automation and energy-efficiency background, with over 10 years of experience helping households improve the way they live — from transitioning older halogen lighting to efficient LED upgrades, to introducing smarter technologies that reduce energy waste across New Zealand and Australian homes.
              </p>
              <p>
                Today, AeroMax Energy continues that journey by helping homeowners move toward practical home electrification — starting with high-efficiency hot water heat pumps, and expanding into solar, battery and smarter energy solutions over time.
              </p>
            </div>

            <div className="mt-8 flex flex-wrap gap-2">
              {[
                { icon: House, t: "Home electrification" },
                { icon: Leaf, t: "10+ yrs energy efficiency" },
                { icon: Sparkles, t: "Smart home background" },
                { icon: Sun, t: "Future solar & battery" },
              ].map((c, i) => (
                <span key={i} className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-[#F8FAFC] border border-[#E2E8F0] text-xs font-semibold text-[#0B132B]">
                  <c.icon className="h-3.5 w-3.5 text-[#10B981]" strokeWidth={2.2} />
                  {c.t}
                </span>
              ))}
            </div>
          </Reveal>

          <Reveal delay={120} className="lg:col-span-5">
            <div className="space-y-4">
              <Card className="rounded-3xl p-7 border-[#E2E8F0] bg-white hover:shadow-xl transition-all">
                <div className="flex items-center gap-3 mb-3">
                  <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#10B981] text-white shrink-0">
                    <Sun className="h-5 w-5" strokeWidth={2} />
                  </span>
                  <p className="font-heading text-xs uppercase tracking-widest text-[#475569] font-semibold">Our Vision</p>
                </div>
                <p className="text-[#0B132B] leading-relaxed">
                  To help create <strong>zero-carbon homes</strong> by making practical, reliable and energy-efficient upgrades easier for everyday households.
                </p>
              </Card>

              <Card className="rounded-3xl p-7 border-[#E2E8F0] bg-[#0B132B] text-white hover:shadow-xl transition-all">
                <div className="flex items-center gap-3 mb-3">
                  <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/10 text-[#10B981] shrink-0">
                    <BadgeCheck className="h-5 w-5" strokeWidth={2} />
                  </span>
                  <p className="font-heading text-xs uppercase tracking-widest text-[#10B981] font-semibold">Our Mission</p>
                </div>
                <p className="text-white/85 leading-relaxed">
                  To guide homeowners through smarter energy upgrades with clear advice, quality products, professional installation pathways and transparent savings conversations.
                </p>
              </Card>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
